import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";
import { Terminal as XTerm } from "xterm";
import { FitAddon } from "xterm-addon-fit";
import { WebLinksAddon } from "xterm-addon-web-links";
import "xterm/css/xterm.css";
import { getTheme } from "../lib/themes";

const DIM = (s) => `\x1b[90m${s}\x1b[0m`;
const RED = (s) => `\x1b[31m${s}\x1b[0m`;
const AMBER = (s) => `\x1b[33m${s}\x1b[0m`;
const GREEN = (s) => `\x1b[32m${s}\x1b[0m`;
const PURPLE = (s) => `\x1b[35m${s}\x1b[0m`;
const CYAN = (s) => `\x1b[36m${s}\x1b[0m`;

function containsAnsi(s) {
  // eslint-disable-next-line no-control-regex
  return /\x1b\[/.test(s);
}

const LiveTerminal = forwardRef(function LiveTerminal({ className, onKeyInput, themeId = "brand" }, ref) {
  const containerRef = useRef(null);
  const termRef = useRef(null);
  const fitRef = useRef(null);
  const roRef = useRef(null);
  const onKeyInputRef = useRef(onKeyInput);

  // Keep ref updated
  useEffect(() => { onKeyInputRef.current = onKeyInput; }, [onKeyInput]);

  // Re-apply theme when themeId changes
  useEffect(() => {
    const term = termRef.current;
    if (!term) return;
    try {
      const t = getTheme(themeId);
      term.options.theme = { ...t.terminal };
    } catch (_) { /* */ }
  }, [themeId]);

  useEffect(() => {
    if (!containerRef.current) return;

    const themeDef = getTheme(themeId).terminal;
    const term = new XTerm({
      theme: themeDef,
      fontFamily:
        "JetBrains Mono, Fira Code, SF Mono, Menlo, Consolas, monospace",
      fontSize: 13,
      lineHeight: 1.3,
      cursorBlink: true,
      cursorStyle: "bar",
      cursorInactiveStyle: "underline",
      disableStdin: false,   // Enable keyboard capture for interactive input
      convertEol: true,
      scrollback: 10000,
      allowProposedApi: true,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.loadAddon(new WebLinksAddon());
    term.open(containerRef.current);

    requestAnimationFrame(() => {
      try { fit.fit(); } catch (_) { /* */ }
      term.write(DIM("noefforts terminal ready — type to interact when running\r\n"));
    });

    // Interactive input handler: fires for all keyboard data when disableStdin=false
    term.onData((data) => {
      const cb = onKeyInputRef.current;
      if (!cb) return; // No active run — discard input

      if (data === "\r") {
        // Enter: echo newline locally and send \n to process
        term.write("\r\n");
        cb("\n");
      } else if (data === "\x7f" || data === "\b") {
        // Backspace: erase last character locally
        term.write("\b \b");
        cb(data);
      } else if (data === "\x03") {
        // Ctrl+C: send interrupt signal via data (backend handles stop separately)
        cb(data);
      } else {
        // Regular printable character(s)
        term.write(data);
        cb(data);
      }
    });

    // ResizeObserver -> auto-fit
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(() => {
        try { fit.fit(); } catch (_) { /* */ }
      });
      ro.observe(containerRef.current);
      roRef.current = ro;
    }

    termRef.current = term;
    fitRef.current = fit;

    return () => {
      try { roRef.current?.disconnect(); } catch (_) { /* */ }
      try { term.dispose(); } catch (_) { /* */ }
      termRef.current = null;
      fitRef.current = null;
    };
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      write(text) {
        termRef.current?.write(text || "");
      },
      writeln(text) {
        termRef.current?.writeln(text || "");
      },
      writeStdout(text) {
        if (text) termRef.current?.write(text);
      },
      writeStderr(text) {
        if (!text) return;
        if (containsAnsi(text)) {
          termRef.current?.write(text);
        } else {
          termRef.current?.write(RED(text));
        }
      },
      writeStageHeader(stage) {
        const label = stage === "compile" ? "compile" : "run";
        termRef.current?.write(
          DIM(`\r\n\u2500\u2500 ${label} \u2500\u2500\r\n`)
        );
      },
      writeCompileFailed(exitCode) {
        termRef.current?.write(
          AMBER(
            `\r\n\u2500\u2500 compile failed (exit ${exitCode}) \u2500\u2500\r\n`
          )
        );
      },
      writeExitFooter({ exitCode, execTimeMs, timedOut, stopped }) {
        let label = `exit code ${exitCode}`;
        if (timedOut) label += " \u00b7 timed out";
        if (stopped) label += " \u00b7 stopped";
        const colorize =
          stopped ? PURPLE :
          timedOut ? AMBER :
          exitCode === 0 ? GREEN :
          RED;
        termRef.current?.write(
          DIM("\r\n\u2500\u2500 ") + colorize(label) +
          DIM(` \u00b7 ${execTimeMs ?? 0}ms \u2500\u2500\r\n`)
        );
      },
      writeError(text) {
        termRef.current?.write(RED(`\r\n[error] ${text || "unknown"}\r\n`));
      },
      writeInfo(text) {
        termRef.current?.write(DIM(text || ""));
      },
      writeInteractiveHint() {
        termRef.current?.write(
          CYAN("\r\n\u25B6 Program is waiting for input — type and press Enter\r\n")
        );
      },
      clear() {
        termRef.current?.clear();
      },
      fit() {
        try { fitRef.current?.fit(); } catch (_) { /* */ }
      },
      focus() {
        termRef.current?.focus();
      },
    }),
    []
  );

  return (
    <div
      ref={containerRef}
      className={"h-full w-full px-2 py-1.5 " + (className || "")}
      style={{ backgroundColor: getTheme(themeId).terminal.background }}
    />
  );
});

export default LiveTerminal;
